import { useEffect, useState } from "react";
import { Stack, Text } from "@mantine/core";
import { Alert, Badge, Button, ConfirmModal, EmptyState, PageHeader, SectionCard } from "@xplatform/design-system";
import { AdminTable, col } from "@components/AdminTable";
import {
    IconPlus,
    IconEdit,
    IconTrash,
    IconCategory,
} from "@tabler/icons-react";
import { useTranslation } from "@hooks/useTranslation";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import { useCategory } from "@hooks/useCategory";
import { CategoryDialog } from "@src/features";
import type { CategoryViewModel } from "@models/category";
import { getLastPartOfUid } from "@src/utils/uidUtil";

export const CategoryManagementPage = () => {
    const { t } = useTranslation();
    const { canManageCategory } = useAdminPermissions();
    const { categories, isLoading, error, fetchCategories, deleteCategory } =
        useCategory();
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editingCategory, setEditingCategory] =
        useState<CategoryViewModel | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingCategory, setDeletingCategory] =
        useState<CategoryViewModel | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const handleOpenCreateDialog = () => {
        setCreateDialogOpen(true);
    };

    const handleCloseCreateDialog = () => {
        setCreateDialogOpen(false);
    };

    const handleEditCategory = (category: CategoryViewModel) => {
        setEditingCategory(category);
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setEditingCategory(null);
    };

    const handleDeleteClick = (category: CategoryViewModel) => {
        setDeletingCategory(category);
        setDeleteDialogOpen(true);
        setDeleteError(null);
    };

    const handleConfirmDelete = async () => {
        if (!deletingCategory) return;
        try {
            await deleteCategory(deletingCategory.id);
            setDeleteDialogOpen(false);
            setDeletingCategory(null);
        } catch (err) {
            setDeleteError(
                err instanceof Error
                    ? err.message
                    : t("admin.categories.messages.deleteFailed"),
            );
        }
    };

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false);
        setDeletingCategory(null);
        setDeleteError(null);
    };

    const handleSuccess = async () => {
        await fetchCategories();
    };

    const columns = [
        col.text<CategoryViewModel>("id", "ID", (c) => getLastPartOfUid(c.id), { width: 96, mono: true, dimmed: true }),
        col.badge<CategoryViewModel>("code", t("admin.table.columns.code"), (c) => (
            <Badge variant="outline" size="sm">{c.code}</Badge>
        ), 140),
        col.text<CategoryViewModel>("name", t("admin.table.columns.name"), (c) => c.name, { width: "18%", strong: true }),
        col.text<CategoryViewModel>("description", t("admin.table.columns.description"), (c) => c.description, { dimmed: true }),
        col.date<CategoryViewModel>("created", t("admin.table.columns.created"), (c) => c.createdAt),
        col.date<CategoryViewModel>("updated", t("admin.table.columns.updated"), (c) => c.updatedAt),
        col.actions<CategoryViewModel>(t("admin.table.columns.actions"), (c) => [
            { label: t("common.edit"), icon: <IconEdit size={16} />, onClick: () => handleEditCategory(c) },
            { label: t("common.delete"), icon: <IconTrash size={16} />, color: "red", onClick: () => handleDeleteClick(c) },
        ]),
    ];

    return (
        <Stack gap="md">
            <PageHeader
                title={t("admin.categories.title")}
                icon={<IconCategory size={28} />}
                actions={canManageCategory ? (
                    <Button leftSection={<IconPlus size={16} />} onClick={handleOpenCreateDialog}>
                        {t("admin.categories.actions.createCategory")}
                    </Button>
                ) : undefined}
            />

            {error && <Alert color="red">{error}</Alert>}

            <SectionCard>
                <AdminTable
                    columns={columns}
                    data={categories}
                    getRowKey={(c) => c.id}
                    loading={isLoading}
                    emptyState={
                        <EmptyState
                            icon={<IconCategory size={32} />}
                            title={t("admin.categories.empty.noCategories")}
                            description={t("admin.categories.empty.description")}
                        />
                    }
                />
            </SectionCard>

            <CategoryDialog
                open={createDialogOpen}
                onClose={handleCloseCreateDialog}
                onSuccess={handleSuccess}
            />

            {editingCategory && (
                <CategoryDialog
                    open={editDialogOpen}
                    onClose={handleCloseEditDialog}
                    onSuccess={handleSuccess}
                    category={editingCategory}
                />
            )}

            <ConfirmModal
                opened={deleteDialogOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title={t("common.delete")}
                confirmLabel={t("common.delete")}
                cancelLabel={t("common.cancel")}
                danger
                size="xs"
                portalProps={{ target: "#admin-portal-root" }}
                message={
                    <>
                        <Text size="sm">
                            {t("admin.categories.messages.deleteConfirm")}
                        </Text>
                        {deleteError && (
                            <Alert color="red" mt="sm">
                                {deleteError}
                            </Alert>
                        )}
                    </>
                }
            />
        </Stack>
    );
};
